import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import {
  registerPasswordChangeRequiredHandler,
  registerSessionExpiredHandler,
  registerVersionChangeHandler,
  STORAGE_AUTH_EPOCH,
} from './api/client'
import { permDirective } from './directives/perm'
import { useAppStore } from './stores/app'
import { useAuthStore } from './stores/auth'

import './styles/fonts.css'

// 顺序有讲究：theme.css 带 Tailwind 的 preflight，必须最先；base.scss 里的存量样式
// 才有机会覆盖 preflight 的重置；element-bridge.css 收尾，把 EP 的 --el-* 接到新令牌。
import './styles/theme.css'
import './styles/base.scss'
import './styles/element-bridge.css'
import { useToast } from '@/composables/useToast'
import { applyFontDensity } from './styles/fontDensity'
import { trackScrollActivity } from '@/utils/scrollActivity'

const toast = useToast()

// 低密度屏用微软雅黑而不是 MiSans（后者没有 hinting，dpr=1 下发虚）。
// 默认栈就是雅黑，这一句只负责在高密度屏上「加上」MiSans，所以低密度屏不会闪。
applyFontDensity()

// 滚动条平时隐形，滚动时才显形（样式在 base.scss，这里只负责打状态类）。
trackScrollActivity()

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.directive('perm', permDirective)

/**
 * 401 拦截器 → store 的桥。
 *
 * 为什么用注册回调而不是让 `api/client.ts` 直接 import store：
 * 那会造成 client → store → client 的循环 import（§9.10.4 的依赖方向：
 * store 可以依赖 api，api 不可以依赖 store）。循环 import 在 Vite 的
 * dev 模式下有时能跑通、build 之后变成 `undefined is not a function`——
 * 是最难查的一类问题。
 */
registerSessionExpiredHandler((reason) => {
  useAuthStore().reset()
  toast.warning(reason === 'revoked' ? '登录状态已失效，请重新登录' : '登录已过期，请重新登录')
  void router.push({
    name: 'login',
    query: { redirect: router.currentRoute.value.fullPath },
  })
})

/**
 * 后端把 `must_change_password=true` 的人挡在门外（403 +
 * `PASSWORD_CHANGE_REQUIRED`）。路由守卫已经在拦，这里补的是它拦不住的两种：
 * 标志位在本标签页打开**之后**才被置上，以及组件里还在跑的轮询。
 * 不接的话页面会满屏「没有权限」，而正确的动作是去改密。
 */
registerPasswordChangeRequiredHandler(() => {
  if (router.currentRoute.value.name === 'profile') return
  toast.warning('请先修改口令：管理员给你的是一次性临时口令')
  void router.push({ name: 'profile', query: { force: 'password' } })
})

registerVersionChangeHandler((from, to) => {
  useAppStore().backendVersionChanged = { from, to }
})

/**
 * 跨标签同步（§9.10.4）：一个标签页登出，其它标签页也要退出。
 * 否则用户在 A 标签退出、切到 B 标签继续点，每个请求都 401，
 * 看起来像"平台坏了"。
 */
window.addEventListener('storage', (e) => {
  if (e.key !== STORAGE_AUTH_EPOCH) return
  const auth = useAuthStore()
  if (!auth.token && auth.loaded) {
    auth.reset()
    void router.push({ name: 'login' })
  }
})

app.mount('#app')
